/**
 * sourceMeta - Metadados compartilhados das fontes de eventos do Calendário Global
 *
 * Centraliza rótulos legíveis, descrições (tooltips) e ícones/cor por fonte,
 * usados em:
 *  - CalendarSidebar (filtro por fonte)
 *  - NewEventPopover (seleção de tipo de novo evento)
 *  - unifiedToActivityItem (lista de eventos)
 *
 * RELEASE:
 *   2026-10-09 v1.2.0 - Adiciona Ordem de Serviço ao catálogo visual.
 *   2026-06-25 v1.1.0 - Adicionado SOURCE_ICON_META (ícone + cor por fonte)
 *   2026-06-25 v1.0.0 - Extração de SOURCE_LABELS/SOURCE_TOOLTIPS do CalendarSidebar
 */

import {
  CalendarPlus,
  CheckSquare,
  Calendar as CalendarIcon,
  MessageSquare,
  CalendarClock,
  Handshake,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { CalendarEventSourceType } from '@/hooks/useCalendarUnifiedEvents';

/** Mapa de rótulos legíveis por tipo de fonte */
export const SOURCE_LABELS: Record<CalendarEventSourceType, string> = {
  manual: 'Eventos manuais',
  booking: 'Agendamento Online',
  activity: 'Atividades CRM',
  task: 'Tarefas',
  service_order: 'Ordens de Serviço',
  google: 'Google Calendar',
  scheduled_message: 'Mensagens Agendadas',
};

/** Tooltip explicativo de cada fonte */
export const SOURCE_TOOLTIPS: Record<CalendarEventSourceType, string> = {
  manual:
    'Eventos criados diretamente no Uaizy pelo botão "Novo evento". Ficam armazenados internamente e não dependem de integração externa.',
  booking:
    'Reservas feitas pelos clientes através das páginas públicas de Agendamento Online (links de booking).',
  activity:
    'Atividades do CRM (ligações, reuniões, follow-ups) vinculadas a deals e contatos.',
  task:
    'Tarefas do módulo de Tarefas com data/prazo definidos, exibidas no dia agendado.',
  service_order:
    'Ordens de Serviço agendadas, com prestador, cliente e tipo de serviço vinculados.',
  google:
    'Eventos sincronizados de uma conta Google Calendar conectada via OAuth. Refletem o calendário externo do usuário em tempo quase real.',
  scheduled_message:
    'Mensagens (WhatsApp, e-mail etc.) programadas para envio futuro, exibidas na data/horário de disparo.',
};

/**
 * Ícone Lucide + variável CSS de cor (token semântico) por fonte.
 * Fonte única consumida pelo sidebar e pela lista, garantindo paridade visual.
 */
export const SOURCE_ICON_META: Record<
  CalendarEventSourceType,
  { Icon: LucideIcon; cssVar: string }
> = {
  manual: { Icon: CalendarPlus, cssVar: '--primary' },
  task: { Icon: CheckSquare, cssVar: '--chart-2' },
  service_order: { Icon: Wrench, cssVar: '--chart-1' },
  google: { Icon: CalendarIcon, cssVar: '--chart-3' },
  scheduled_message: { Icon: MessageSquare, cssVar: '--chart-4' },
  booking: { Icon: CalendarClock, cssVar: '--chart-5' },
  activity: { Icon: Handshake, cssVar: '--chart-1' },
};

